import {
  FileFolderType,
  ITab,
  WebformState,
  WebformStateCondition,
} from '@qodly/shared';
import { AppState } from '~store/modules';
import { IRootState } from '~store/modules/root/types';
import { EDebuggerState } from '~store/modules/debugger/types';
import { ModelNode, ServerModel } from '~store/modules/model/types';
import { isEqual } from 'lodash';

export const CATALOG_REFRESH_DELAY = 2000;

export const DEFAULT_CACHE: TCachedState = {
  tabs: [],
  activeTab: '',
  recent: [],
  roles: {
    roles: {},
    privileges: {},
    flags: { touched: false },
    selectedTab: 'roles',
    forceLogin: false,
  },
  sharedCSS: {
    state: 'loading',
    styles: [],
  },
  debugger: {
    meta: {
      state: EDebuggerState.DETACHED,
    },
    list: [],
    sessions: [],
    expressions: [],
  },
  webforms: {
    templates: {
      selectedGroup: '',
      data: [],
    },
    components: [],
    inspector: {
      css: {
        active: false,
      },
      datasource: {
        active: false,
      },
    },
    datasources: {
      theme: 'grey',
    },
    flags: { 'sidebar.section.resizable': true },
  },
  tasks: [],
};

export type TCachedState = Pick<IRootState, 'tabs' | 'activeTab' | 'recent'> &
  Pick<AppState, 'roles' | 'debugger' | 'webforms' | 'sharedCSS' | 'tasks'>;
const PREFRENCES = '4DWS_PREFS';

const getAppStateID = (baseID: string) => {
  return `APP_STATE_${baseID}`;
};

const getBaseID = () => {
  return window?.$$datastores?.ds?.getBaseID() || '';
};

export function saveState(state: TCachedState): void {
  try {
    const baseID = getBaseID();

    // TODO:
    // we need to generate and save in file
    // we can't do it here because it is async

    const serializedState = JSON.stringify(state);
    localStorage.setItem(getAppStateID(baseID), serializedState);
  } catch (e) {
    console.error(e);
  }
}

export function savePrefrences(state: { [key: string]: any }): void {
  try {
    const serializedState = JSON.stringify(state);
    localStorage.setItem(PREFRENCES, serializedState);
  } catch (e) {
    console.error(e);
  }
}

export function getCashedPrefrences() {
  try {
    const serializedPrefs = localStorage.getItem(PREFRENCES);
    if (serializedPrefs) return JSON.parse(serializedPrefs);
  } catch (e) {
    console.error(e);
  }
}

export function getCachedState(): TCachedState {
  const baseID = getBaseID();
  const serializedState = localStorage.getItem(getAppStateID(baseID));
  if (serializedState) {
    const state = JSON.parse(serializedState) as TCachedState;
    state.tabs = state.tabs.map((tab) => ({
      ...tab,
      flags: { ...tab.flags, enabled: true },
    }));
    return state;
  }

  return DEFAULT_CACHE;
}

export function getModalTitle(tab: ITab) {
  const { name, type, path } = tab;
  const ext = path.split('.').at(-1);
  switch (type) {
    case FileFolderType.WEBFORM:
    case FileFolderType.ROLES:
    case FileFolderType.HTTP_HANDLERS:
    case FileFolderType.SETTINGS:
    case FileFolderType.MODEL:
      return name;
    case FileFolderType.METHOD:
    case FileFolderType.CLASS:
    default:
      return ext ? `${name}.${ext}` : name;
  }
}

export const deserializeModel = (data: ServerModel | undefined) => {
  if (!data) return [];
  // generate dataclass nodes
  const dataclassMap = new Map<string, ModelNode>();

  data.dataClasses.forEach((dataclass) => {
    const dataclassProperties = dataclass.extraProperties || {};

    const dc: ModelNode = {
      id: dataclass.className,
      type: 'table',
      position: {
        x: dataclassProperties.position?.x || 0,
        y: dataclassProperties.position?.y || 0,
      },
      dragHandle: '.custom-drag-handle',
      data: dataclass,
    };

    dataclassMap.set(dataclass.name, dc);
  });
  // generate edges
  return Array.from(dataclassMap.values());
};

export function isJSON(extension?: string): boolean {
  if (!extension) return false;
  return ['json', '4dmodel', 'webform', 'mform', 'qodoc'].includes(
    extension.toLowerCase(),
  );
}

export const refactorConditions = (
  condition: WebformStateCondition,
  stateLabel: string | undefined,
  newValue: string = '',
): WebformStateCondition => {
  if (!stateLabel) return condition;
  if (condition.type === 'currentState' && condition.value === stateLabel) {
    return { ...condition, value: newValue };
  } else if (condition.type === 'combination' && condition.conditions) {
    return {
      ...condition,
      conditions: condition.conditions.map(
        (nestedCondition: WebformStateCondition) =>
          refactorConditions(nestedCondition, stateLabel, newValue),
      ),
    };
  }
  return condition;
};

export function refactorWebform(content: ITab['content'], state: WebformState) {
  const { id, label } = state;
  return {
    ...content,
    components: Object.entries(content.components || {}).reduce(
      (prev, [key, value]) => {
        return {
          ...prev,
          [key]: {
            ...(value as any),
            // remove custom props of the deleted state
            custom: Object.entries((value as any).custom).reduce(
              (prev, [key, val]) => {
                if (key === `states:${id}`) {
                  return prev;
                }
                return {
                  ...prev,
                  [key]: val,
                };
              },
              {},
            ),
            // refactor node events
            props: Object.entries((value as any).props).reduce(
              (prev, [key, val]) => {
                let events = val;
                if (key === 'events') {
                  events = (events as webforms.WEvent[]).map((event) => {
                    if (event.type === 'simple-action') {
                      return {
                        ...event,
                        state: {
                          ...event.state,
                          states: event.state?.states?.filter(
                            (state) => state !== id,
                          ),
                        },
                      };
                    }
                    return event;
                  });
                }
                return {
                  ...prev,
                  [key]: events,
                };
              },
              {},
            ),
          },
        };
      },
      {},
    ),
    metadata: {
      ...content.metadata,
      // delete state
      states: content.metadata.states
        .filter((state: WebformState) => state?.id !== id)
        .map((state: WebformState) => {
          if (state.conditions) {
            return {
              ...state,
              conditions: refactorConditions(state.conditions, label),
            };
          }
          return state;
        }),
    },
  };
}

export const selectorEqualityFn = (left: unknown, right: unknown) =>
  isEqual(left, right);
